import { Box, Checkbox } from '@chakra-ui/react';
import { CellContext, HeaderContext } from '@tanstack/react-table';
import { EditableTableMeta, TableEntry } from './Table';

export function RowSelectHeader<D extends TableEntry>({
  table: {
    getRowModel,
    options: { meta },
  },
}: HeaderContext<D, unknown>) {
  const isEditing = (meta as EditableTableMeta | undefined)?.isEditing;

  const areAllRowsSelected = () => {
    const allRows = getRowModel().rows;
    return allRows.every(
      (row) =>
        row.getIsSelected() &&
        row.subRows &&
        row.subRows.every((subRow) => subRow.getIsSelected())
    );
  };
  const areSomeRowsSelected = () => {
    const allRows = getRowModel().rows;
    return allRows.some(
      (row) =>
        row.getIsSelected() ||
        (row.subRows && row.subRows.some((subRow) => subRow.getIsSelected()))
    );
  };

  return (
    <Checkbox
      aria-label="Select all rows"
      size="md"
      mt={1}
      variant="rounded-noanimate"
      isChecked={areAllRowsSelected()}
      isIndeterminate={areSomeRowsSelected() && !areAllRowsSelected()}
      onChange={() => {
        const shouldSelectAll = !areAllRowsSelected();
        getRowModel().rows.forEach((row) => {
          row.toggleSelected(shouldSelectAll);
          if (row.subRows) {
            row.subRows.forEach((subRow) => {
              subRow.toggleSelected(shouldSelectAll);
            });
          }
        });
      }}
      onDoubleClick={(event) => {
        // This is to prevent double-click to inadvertently open the row for editing
        event.stopPropagation();
      }}
      isDisabled={isEditing}
    />
  );
}

export function RowSelectCell<D extends TableEntry>({
  table: {
    options: { meta },
  },
  row,
}: CellContext<D, unknown>) {
  const isEditing = (meta as EditableTableMeta | undefined)?.isEditing;

  return (
    <Box display="inline-block" onClick={(e) => e.stopPropagation()}>
      <Checkbox
        aria-label={`Select row ${row.original.name}`}
        size="md"
        variant="rounded-noanimate"
        isChecked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        onDoubleClick={(event) => {
          // This is to prevent double-click to inadvertently open the row for editing
          event.stopPropagation();
        }}
        isDisabled={isEditing}
      />
    </Box>
  );
}
